import {
  Add as AddIcon,
  Assignment as AssignmentIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomDataGrid from '../components/CustomDataGrid';
import DietProgramModal from '../components/DietProgramModal';
import Loading from '../components/Loading';
import { useDietitianData } from '../hooks/useDietitianData';
import {
  CreateDietProgramRequest,
  DietProgram,
  dietProgramApi,
} from '../services/dietProgramApi';
import { colors } from '../theme';

const DietitianProgramsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    dietitianId,
    clients,
    dietPrograms,
    loading,
    setDietPrograms,
    showSuccess,
    showError,
  } = useDietitianData({
    fetchClients: true,
    fetchAppointments: false,
    fetchDietPrograms: true,
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    {}
  );
  const [isDietProgramModalOpen, setIsDietProgramModalOpen] = useState(false);

  const isMobile = useMediaQuery('(max-width: 600px)');

  const handleAddDietProgram = async (
    programData: CreateDietProgramRequest
  ) => {
    try {
      await dietProgramApi.createDietProgram(programData).then((res) => {
        setDietPrograms((prev) => [...prev, res.dietProgram]);
        showSuccess('Diyet programı başarıyla oluşturuldu!', 'Program Eklendi');
        setIsDietProgramModalOpen(false);
      });
    } catch (err) {
      showError(
        'Diyet programı eklenirken bir sorun oluştu. Lütfen tekrar deneyin.',
        'Ekleme Hatası'
      );
      console.error('Error creating diet program:', err);
    }
  };

  // Filtrelenmiş programlar
  const filteredDietPrograms = dietPrograms.filter((program) => {
    const query = searchQuery.toLowerCase();
    const matchesSearch =
      !searchQuery ||
      program.programName.toLowerCase().includes(query) ||
      program.clientName.toLowerCase().includes(query) ||
      program.programType.toLowerCase().includes(query) ||
      program.status.toLowerCase().includes(query);

    if (!matchesSearch) {
      return false;
    }

    // Column filters
    for (const [field, filterValue] of Object.entries(columnFilters)) {
      if (!filterValue) continue;

      const value = String(
        program[field as keyof DietProgram] || ''
      ).toLowerCase();

      if (!value.includes(filterValue.toLowerCase())) {
        return false;
      }
    }

    return true;
  });

  const handleColumnFilter = (field: string, value: string) => {
    setColumnFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // DataGrid columns
  const columns: GridColDef[] = [
    {
      field: 'programName',
      headerName: 'Diyet Programı Adı',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<DietProgram>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: colors.primary.main,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '1.1rem',
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(46, 125, 50, 0.3)',
            }}
          >
            <AssignmentIcon sx={{ fontSize: 24 }} />
          </Box>
          <Typography variant="body1" fontWeight={600} fontSize="1rem">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'clientName',
      headerName: 'Danışan',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams<DietProgram>) => (
        <Typography
          variant="body2"
          fontSize="0.95rem"
          sx={{ color: colors.primary.dark, fontWeight: 600 }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'programType',
      headerName: 'Tür',
      flex: 1,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams<DietProgram>) => (
        <Chip
          label={params.value}
          sx={{
            bgcolor: colors.background.light,
            fontWeight: 600,
            fontSize: '0.9rem',
            height: 36,
            px: 1,
          }}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Durum',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<DietProgram>) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            bgcolor:
              params.value === 'Aktif'
                ? colors.primary.main
                : params.value === 'Tamamlandı'
                ? colors.primary.dark
                : colors.text.secondary,
            color: 'white',
            fontWeight: 600,
            fontSize: '0.9rem',
            height: 36,
            px: 1,
          }}
        />
      ),
    },
    {
      field: 'startDate',
      headerName: 'Başlangıç',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<DietProgram>) => (
        <Typography variant="body2" color="text.secondary" fontSize="0.95rem">
          {params.value
            ? new Date(params.value).toLocaleDateString('tr-TR')
            : '-'}
        </Typography>
      ),
    },
    {
      field: 'endDate',
      headerName: 'Bitiş',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<DietProgram>) => (
        <Typography variant="body2" color="text.secondary" fontSize="0.95rem">
          {params.value
            ? new Date(params.value).toLocaleDateString('tr-TR')
            : '-'}
        </Typography>
      ),
    },
    {
      field: 'targetWeight',
      headerName: 'Hedef Kilo',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<DietProgram>) => (
        <Typography variant="body2" fontSize="0.95rem">
          {params.value ? `${params.value} kg` : '-'}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Oluşturulma',
      flex: 1,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams<DietProgram>) => (
        <Typography variant="body2" color="text.secondary" fontSize="0.95rem">
          {params.value
            ? new Date(params.value).toLocaleDateString('tr-TR')
            : '-'}
        </Typography>
      ),
    },
  ];

  const handleRowClick = (params: any) => {
    if (params.row.id) {
      navigate(`/dietitian/programs/${params.row.id}`);
    }
  };

  if (loading) {
    return (
      <Loading
        size="large"
        variant="wave"
        mode="inline"
        message="Diyet programları yükleniyor..."
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          height: 'calc(100vh - 24px)',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '100vw',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            bgcolor: 'white',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            mb: 3,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              flex={1}
              minWidth={0}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: colors.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <AssignmentIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box minWidth={0} flex={1}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: colors.text.primary }}
                  noWrap
                >
                  Diyet Programlarım
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {filteredDietPrograms.length} / {dietPrograms.length} program
                </Typography>
              </Box>
            </Box>
            {!isMobile && (
              <Button
                variant="contained"
                onClick={() => setIsDietProgramModalOpen(true)}
                sx={{
                  bgcolor: colors.primary.main,
                  color: 'white',
                  px: 3,
                  py: 1.5,
                  width: 300,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(46, 125, 50, 0.3)',
                  ml: 2,
                  flexShrink: 0,
                  '&:hover': {
                    bgcolor: colors.hover.primary,
                    boxShadow: '0 6px 16px rgba(46, 125, 50, 0.4)',
                  },
                }}
              >
                Yeni Program Ekle
              </Button>
            )}
            {isMobile && (
              <IconButton
                onClick={() => setIsDietProgramModalOpen(true)}
                sx={{
                  bgcolor: colors.primary.main,
                  color: 'white',
                  width: 36,
                  height: 36,
                  ml: 1,
                  flexShrink: 0,
                  '&:hover': {
                    bgcolor: colors.hover.primary,
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {/* Search Bar */}
          <TextField
            fullWidth
            placeholder="Program ara (isim, danışan, tür, durum...)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: colors.background.light,
                borderRadius: 2,
                '&:hover': {
                  bgcolor: 'white',
                },
                '&.Mui-focused': {
                  bgcolor: 'white',
                },
              },
            }}
          />
        </Box>

        {/* DataGrid */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <CustomDataGrid
            rows={filteredDietPrograms.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            )}
            columns={columns}
            loading={loading}
            onRowClick={handleRowClick}
            onColumnFilter={handleColumnFilter}
            noRowsLabel="Henüz diyet programı bulunmuyor"
          />
        </Box>
      </Box>

      <DietProgramModal
        open={isDietProgramModalOpen}
        onClose={() => setIsDietProgramModalOpen(false)}
        onSave={handleAddDietProgram}
        clients={clients as any}
        dietitianId={dietitianId}
      />
    </>
  );
};

export default DietitianProgramsPage;
